"use strict";

const NEWS_PATH = "/news";
const NEWS_ID_PATTERN =
  /^news-\d{4}-\d{2}-\d{2}-[a-z0-9]+(?:-[a-z0-9]+)*$/;
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const requireNonEmptyString = (value, label) => {
  if (typeof value !== "string" || !value.trim()) {
    throw new Error(`Missing or invalid ${label}.`);
  }

  return value.trim();
};

const parseHttpsUrl = (value, label) => {
  const urlValue = requireNonEmptyString(value, label);
  let url;

  try {
    url = new URL(urlValue);
  } catch (error) {
    throw new Error(`Invalid ${label}.`);
  }

  if (url.protocol !== "https:") {
    throw new Error(`${label} must use HTTPS.`);
  }

  return url;
};

const isRealIsoDate = (value) => {
  if (typeof value !== "string") {
    return false;
  }

  const match = ISO_DATE_PATTERN.exec(value);
  if (!match) {
    return false;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));

  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
};

const validateNewsContext = ({ canonicalUrl, page, site, newsItems }) => {
  if (!page || typeof page !== "object") {
    throw new Error("Missing news page metadata.");
  }
  if (!site || typeof site !== "object") {
    throw new Error("Missing site metadata.");
  }
  if (!site.artist || typeof site.artist !== "object") {
    throw new Error("Missing news author metadata.");
  }
  if (!Array.isArray(newsItems) || !newsItems.length) {
    throw new Error("News structured data requires at least one item.");
  }

  requireNonEmptyString(page.title, "news page title");
  requireNonEmptyString(page.description, "news page description");

  const siteOrigin = parseHttpsUrl(site.origin, "site origin");
  if (
    siteOrigin.origin !== site.origin ||
    siteOrigin.pathname !== "/" ||
    siteOrigin.search ||
    siteOrigin.hash
  ) {
    throw new Error("Site origin must be an HTTPS origin without a path.");
  }

  const canonical = parseHttpsUrl(canonicalUrl, "news canonical URL");
  if (
    canonical.origin !== siteOrigin.origin ||
    canonical.pathname !== NEWS_PATH ||
    canonical.search ||
    canonical.hash ||
    canonical.href !== `${siteOrigin.origin}${NEWS_PATH}`
  ) {
    throw new Error("News canonical URL must match the canonical /news route.");
  }

  const artistId = parseHttpsUrl(site.artist.id, "news author id");
  const artistUrl = parseHttpsUrl(site.artist.url, "news author URL");
  if (
    artistId.origin !== siteOrigin.origin ||
    artistUrl.origin !== siteOrigin.origin
  ) {
    throw new Error("News author URLs must use the site origin.");
  }
  requireNonEmptyString(site.artist.name, "news author name");

  const ids = new Set();
  newsItems.forEach((item, index) => {
    if (!item || typeof item !== "object") {
      throw new Error(`Invalid news item at position ${index + 1}.`);
    }
    if (!NEWS_ID_PATTERN.test(item.id)) {
      throw new Error(`Invalid news id: ${item.id}`);
    }
    if (ids.has(item.id)) {
      throw new Error(`Duplicate news id: ${item.id}`);
    }
    ids.add(item.id);

    if (!isRealIsoDate(item.datePublished)) {
      throw new Error(`Invalid news publication date: ${item.datePublished}`);
    }
    if (
      index > 0 &&
      newsItems[index - 1].datePublished < item.datePublished
    ) {
      throw new Error(
        "News items must be ordered by publication date descending."
      );
    }

    requireNonEmptyString(item.headline, `headline for ${item.id}`);
    requireNonEmptyString(item.summary, `summary for ${item.id}`);
  });

  return canonical.href;
};

const createNewsStructuredData = ({ canonicalUrl, page, site, newsItems }) => {
  const canonical = validateNewsContext({
    canonicalUrl,
    page,
    site,
    newsItems,
  });
  const author = {
    "@type": "Person",
    "@id": site.artist.id,
    name: site.artist.name,
    url: site.artist.url,
  };

  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${canonical}#webpage`,
    url: canonical,
    name: page.title,
    description: page.description,
    mainEntity: {
      "@type": "ItemList",
      "@id": `${canonical}#news-list`,
      itemListOrder: "https://schema.org/ItemListOrderDescending",
      numberOfItems: newsItems.length,
      itemListElement: newsItems.map((item, index) => {
        const itemUrl = `${canonical}#${item.id}`;

        return {
          "@type": "ListItem",
          position: index + 1,
          name: item.headline,
          url: itemUrl,
          item: {
            "@type": "CreativeWork",
            "@id": itemUrl,
            url: itemUrl,
            name: item.headline,
            description: item.summary,
            datePublished: item.datePublished,
            author,
          },
        };
      }),
    },
  };
};

const serializeNewsStructuredData = (structuredData) => {
  if (!structuredData || typeof structuredData !== "object") {
    throw new Error("News structured data must be an object.");
  }

  return JSON.stringify(structuredData)
    .replace(/</g, "\\u003c")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");
};

module.exports = {
  createNewsStructuredData,
  serializeNewsStructuredData,
};
